import { useTranslation } from 'react-i18next'

import {
  downloadFileDocumentById,
  getFileDocumentSignedLink,
  massDownloadFile,
} from '../../../../api'
import { EMPTY_LIST } from '../../../../constants/common'
import { FILE_SCOPE_USER } from '../../../../constants/file'
import {
  useNotification,
  useOutboundUuid,
  useUserData,
} from '../../../../hooks'
import { isApiAcceptedStatus } from '../../../../utils/api'
import { getAsyncTaskId } from '../../../../utils/async'
import { getItemsIds } from '../../../../utils/common'
import { downloadFileByUrl } from '../../../../utils/file'
import { getBadRequestErrorNotificationKey } from '../../../../utils/validation'
import { ConfirmationModal } from '../../../Confirmation'

import {
  checkAsyncTask,
  checkDownloadArchiveTaskStatus,
  isOverLimitFiles,
} from './useFilesDownloadAll.utils'

export function useFilesDownloadAll({
  downloadFilesApi,
  files = EMPTY_LIST,
  onCloseModal,
  onSetModal,
}) {
  const { t } = useTranslation()

  const {
    handleAddErrorNotification,
    handleAddInfoNotification,
    handleAddSuccessNotification,
  } = useNotification()

  const outboundUuid = useOutboundUuid()

  const userData = useUserData()

  const isOverLimit = isOverLimitFiles(files)

  function handleErrorDownload(error) {
    handleAddErrorNotification(
      getBadRequestErrorNotificationKey(
        error,
        'Notification.CommonErrorAsyncDownloadKey'
      )
    )
  }

  async function getArchiveDownloadUrl(fileId) {
    if (outboundUuid) {
      const { data } = await downloadFileDocumentById({
        id: fileId,
        outboundUuid,
      })

      return data
    }

    const { data } = await getFileDocumentSignedLink({
      id: fileId,
      params: { object_id: userData?.id, scope: FILE_SCOPE_USER },
    })

    return data?.url
  }

  function downloadArchive(fileId) {
    getArchiveDownloadUrl(fileId)
      .then(url => {
        if (url) {
          handleAddSuccessNotification(
            'Notification.CommonSuccessAsyncDownloadKey'
          )
          downloadFileByUrl(url)
        } else {
          handleErrorDownload()
        }
      })
      .catch(handleErrorDownload)
  }

  function finishDownloadHandler(data) {
    const resultError = data?.error

    if (!data || resultError) {
      resultError
        ? handleAddErrorNotification(resultError)
        : handleErrorDownload()
    } else {
      downloadArchive(data.result_archive_id)
    }
  }

  function handleStartCheckStatus(task, status) {
    handleAddInfoNotification('Notification.CommonAsyncTitleStartDownloadKey')

    if (isApiAcceptedStatus(status)) {
      checkAsyncTask({ handler: finishDownloadHandler, outboundUuid, task })
    } else {
      checkDownloadArchiveTaskStatus({
        handler: finishDownloadHandler,
        outboundUuid,
        task,
      })
    }
  }

  function startDownloadFiles() {
    onCloseModal()

    const downloadRequest = downloadFilesApi
      ? downloadFilesApi({ outboundUuid })
      : massDownloadFile({
          data: { file_ids: getItemsIds(files) },
          outboundUuid,
        })

    downloadRequest
      .then(({ data }) => {
        const asyncTask = getAsyncTaskId(data)

        if (asyncTask) {
          handleStartCheckStatus(asyncTask)

          return
        }

        finishDownloadHandler(data)
      })
      .catch(handleErrorDownload)
  }

  function handleClickDownloadAll() {
    onSetModal(
      <ConfirmationModal
        actionButtonsProps={{
          ...(isOverLimit ? { Cancel: null } : null),
          submitText: t(
            isOverLimit ? 'Modal.CloseBtnKey' : 'Modal.DownloadAllFilesBtnKey'
          ),
        }}
        description={t(
          isOverLimit
            ? 'Modal.AttentionFilesOutOfLimitMessageKey'
            : 'Modal.AttentionFilesDownloadMessageKey'
        )}
        title={t('Common.AttentionModalTitleKey')}
        onCancel={onCloseModal}
        onSubmit={isOverLimit ? onCloseModal : startDownloadFiles}
      />
    )
  }

  return {
    handleClickDownloadAll,
    handleErrorDownload,
    handleStartCheckStatus,
    isOverLimitFiles,
  }
}
